/**
 * Service Registry
 *
 * Central registry of all services monitored by Merlin's health dashboard.
 * Contains plain English descriptions for non-technical users.
 */

export const SERVICE_CATEGORIES = {
  ORDER_FLOW: {
    label: 'Order Flow Services',
    description: 'Critical services for processing orders and inventory'
  },
  SUPPORT: {
    label: 'Support Services',
    description: 'AI, communication, and search services'
  },
  INFRASTRUCTURE: {
    label: 'Infrastructure',
    description: 'EC2 workers and data pipelines'
  }
};

export const SERVICE_REGISTRY = {
  spApi: {
    id: 'spApi',
    name: 'Amazon SP-API',
    category: 'ORDER_FLOW',
    description: 'Connects Merlin to Amazon Seller Central for listing products, managing orders, and tracking inventory on Amazon.',
    firestorePath: 'systemHealth/services/spApi/current',
    monitored: true
  },
  shopify: {
    id: 'shopify',
    name: 'Shopify',
    category: 'ORDER_FLOW',
    description: 'Syncs product data to your Shopify store including prices, inventory, and descriptions.',
    firestorePath: 'systemHealth/services/shopify/current',
    monitored: true
  },
  shipStation: {
    id: 'shipStation',
    name: 'ShipStation',
    category: 'ORDER_FLOW',
    description: 'Handles shipping label creation and order fulfillment tracking.',
    firestorePath: 'systemHealth/services/shipStation/current',
    monitored: true
  },
  sellerActive: {
    id: 'sellerActive',
    name: 'SellerActive',
    category: 'ORDER_FLOW',
    description: 'Multi-channel listing and repricing tool for marketplace management.',
    firestorePath: 'systemHealth/services/sellerActive/current',
    monitored: true
  },
  claude: {
    id: 'claude',
    name: 'Claude AI',
    category: 'SUPPORT',
    description: 'Powers product enrichment with AI-generated titles, descriptions, and keywords.',
    firestorePath: 'systemHealth/services/claude/current',
    monitored: true
  },
  openai: {
    id: 'openai',
    name: 'OpenAI',
    category: 'SUPPORT',
    description: 'Generates embeddings for taxonomy matching. Used rarely - low activity is normal.',
    firestorePath: 'systemHealth/services/openai/current',
    monitored: true
  },
  postmark: {
    id: 'postmark',
    name: 'Postmark',
    category: 'SUPPORT',
    description: 'Sends transactional emails for order confirmations and alerts.',
    firestorePath: 'systemHealth/services/postmark/current',
    monitored: true
  },
  twilio: {
    id: 'twilio',
    name: 'Twilio',
    category: 'SUPPORT',
    description: 'Sends SMS notifications for urgent alerts and updates.',
    firestorePath: 'systemHealth/services/twilio/current',
    monitored: true
  },
  // Algolia is frontend-only and cannot be monitored from backend
  // Removed from active monitoring - was always showing "unknown"
  algolia: {
    id: 'algolia',
    name: 'Algolia',
    category: 'SUPPORT',
    description: 'Frontend search service - not monitored (client-side only).',
    firestorePath: 'systemHealth/services/algolia/current',
    monitored: false
  },
  ec2Worker: {
    id: 'ec2Worker',
    name: 'EC2 Worker',
    category: 'INFRASTRUCTURE',
    description: 'Runs scheduled jobs for inventory sync, ShipStation updates, and order processing on AWS.',
    firestorePath: 'systemHealth/services/ec2Worker/current',
    monitored: true
  },
  ibmiPipeline: {
    id: 'ibmiPipeline',
    name: 'IBMi Inventory Pipeline',
    category: 'INFRASTRUCTURE',
    description: 'Syncs inventory data from the IBMi warehouse system to Firebase and ShipStation.',
    firestorePath: 'systemHealth/services/ibmiPipeline/current',
    monitored: true
  }
};

/**
 * Get services filtered by category
 * @param {string} category - Category key (ORDER_FLOW or SUPPORT)
 * @returns {Array} Array of service objects
 */
export function getServicesByCategory(category) {
  return Object.values(SERVICE_REGISTRY).filter(s => s.category === category);
}

/**
 * Get all currently monitored services
 * @returns {Array} Array of monitored service objects
 */
export function getMonitoredServices() {
  return Object.values(SERVICE_REGISTRY).filter(s => s.monitored);
}

/**
 * Get all services (both monitored and not-yet-monitored)
 * @returns {Array} Array of all service objects
 */
export function getAllServices() {
  return Object.values(SERVICE_REGISTRY);
}
